/**
 * Login page — shown by AuthGate (App.tsx) whenever AuthProvider resolves
 * mode 'login' (CLICKPAY_AUTH=on and no valid session), both on boot and
 * after a mid-session expiry. Rendered IN PLACE of the app shell: the URL
 * is preserved, so a successful login lands on the originally requested
 * route with no redirect bookkeeping.
 *
 * Design language: AdBetter restyle conventions — violet brand palette
 * (--color-brand-*), rounded-md controls, lucide icons, gray-50 canvas.
 */
import { useState, type FormEvent } from 'react'
import { AlertCircle, Loader2, Lock, LogIn, Mail } from 'lucide-react'
import { useAuth } from '../lib/auth'

export default function LoginPage() {
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await login(email.trim(), password, remember)
      // Success: AuthProvider flips mode to 'authed' and AuthGate swaps
      // this page out for the app at the current URL. Nothing to do here.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setBusy(false)
    }
  }

  const inputCls =
    'w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm ' +
    'text-gray-900 placeholder-gray-400 bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Brand block — mirrors the sidebar header */}
        <div className="text-center mb-6">
          <div className="mx-auto w-12 h-12 rounded-xl bg-brand-600 text-white flex items-center justify-center mb-3 shadow-sm">
            <LogIn size={22} aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">ClickPay</h1>
          <p className="text-xs text-gray-500 mt-0.5">Better Basics USA</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-sm font-medium text-gray-900 mb-4">
            Sign in to continue
          </h2>

          {error && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                Email
              </span>
              <span className="relative block">
                <Mail
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                  disabled={busy}
                />
              </span>
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">
                Password
              </span>
              <span className="relative block">
                <Lock
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputCls}
                  disabled={busy}
                />
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 select-none cursor-pointer">
              <input
                type="checkbox"
                name="remember"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                disabled={busy}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span>Remember me on this device</span>
            </label>

            <button
              type="submit"
              disabled={busy || !email.trim() || !password}
              className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy
                ? (<><Loader2 size={15} className="animate-spin" aria-hidden /> Signing in…</>)
                : (<><LogIn size={15} aria-hidden /> Sign in</>)}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Sessions last 30 days. Ask an admin if you need an account or a
          password reset.
        </p>
      </div>
    </div>
  )
}
